import React, { ReactElement, useEffect, useReducer } from "react";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";
import { enviroment } from "../../../enviroment";
import {
  isProductPendingStale,
  LogoActionLockVariant,
  PRODUCT_PENDING_STALE_AFTER_MS,
} from "../../../utils/functions/productPending";
import { parseUtcStamp } from "../../../utils/functions/storageUpdatedAt";

// Status line shown while the API's product-refresh worker is still writing
// a logo's (or a design's) fresh product and prices (flag
// REACT_APP_PRODUCT_PENDING_ENABLED, BaseLogo.productPending /
// Design.pricePending). Renders NOTHING unless the flag is on AND `pending`
// is true - the flag-off DOM is byte-identical to today. After 10 minutes
// (sinceUtc) the text switches to "Prices delayed" so the customer knows to
// contact the supplier - and the badge keeps that appointment itself: one
// timer for the moment the stamp turns 10 minutes old re-renders it, so a
// row nobody touches (list poll budget spent, no re-render from anywhere)
// still stops pulsing and reads "delayed" on time instead of "working"
// forever. No timer with the flag off, when not pending, or once stale.
//
// Look (11/9): the same family as the status column it sits under - the
// 10 px semibold status text in the pending tone (text-status-pending-1,
// ApprovalStatusHeader) with the participants' 0.25rem dot in front, no
// pill, no fill, no border (the earlier amber pill outweighed the status
// text next to it). The dot pulses (motion-safe) while the worker is still
// expected - the "working" signal - and goes grey with the text once the
// state is stale. Dot-to-text gap = the participants' space-x-2 (gap-2), so
// the text starts on the same x as the status lines right above it. Same
// component in the list, the popup, the editor, the design list/popup, the
// cart and the order confirmation, so they all agree. AwaitingImageBadge
// (sibling) keeps its pill - out of scope here.
//
// variant "upload" (flag REACT_APP_CLONE_INSTANT_ENABLED,
// BaseLogo.uploadPending): the same line for a clone whose own supplier
// identity the API's clone-upload worker still owes - "Preparing logo…" /
// "Logo preparation delayed" - gated on ITS flag, so each variant is inert
// with its own flag off.
function variantEnabled(variant: LogoActionLockVariant): boolean {
  return variant === "upload"
    ? enviroment.cloneInstantEnabled
    : enviroment.productPendingEnabled;
}

const BADGE_KEYS: Record<
  LogoActionLockVariant,
  { text: string; tooltip: string; stale: string; staleTooltip: string; action: string }
> = {
  product: {
    text: "product_pending",
    tooltip: "product_pending_tooltip",
    stale: "product_pending_stale",
    staleTooltip: "product_pending_stale_tooltip",
    action: "product_pending_action_tooltip",
  },
  upload: {
    text: "upload_pending",
    tooltip: "upload_pending_tooltip",
    stale: "upload_pending_stale",
    staleTooltip: "upload_pending_stale_tooltip",
    action: "upload_pending_action_tooltip",
  },
};

export function ProductPendingBadge({
  pending,
  sinceUtc,
  className,
  variant = "product",
}: {
  pending: boolean;
  sinceUtc?: string | null;
  className?: string;
  variant?: LogoActionLockVariant;
}): ReactElement | null {
  const { t } = useTranslation();
  const enabled = variantEnabled(variant) && pending;
  const stale = isProductPendingStale(sinceUtc);
  // Re-render exactly once, when the stamp turns 10 minutes old, so the
  // "working" pulse cannot outlive the state it signals. Capped at the stale
  // window (a stamp from the future would otherwise arm a timer that never
  // matters); cleared on unmount or when the stamp changes.
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    if (!enabled || stale || !sinceUtc) return;
    const since = parseUtcStamp(sinceUtc);
    if (!Number.isFinite(since)) return;
    const remaining = since + PRODUCT_PENDING_STALE_AFTER_MS - Date.now();
    const timer = setTimeout(
      rerender,
      Math.min(Math.max(remaining, 0), PRODUCT_PENDING_STALE_AFTER_MS) + 1
    );
    return () => clearTimeout(timer);
  }, [enabled, stale, sinceUtc]);
  if (!enabled) return null;
  const keys = BADGE_KEYS[variant];
  return (
    <Tooltip
      title={t(stale ? keys.staleTooltip : keys.tooltip)}
      placement="top"
    >
      <span
        data-testid={
          variant === "upload" ? "upload-pending-badge" : "product-pending-badge"
        }
        className={`inline-flex items-center gap-2 whitespace-nowrap text-xs font-semibold ${
          stale ? "text-gray-600" : "text-status-pending-1"
        } ${className ?? ""}`}
      >
        <span
          aria-hidden="true"
          className={`h-[0.25rem] w-[0.25rem] shrink-0 rounded-full ${
            stale
              ? "bg-gray-400"
              : "bg-status-pending-1 motion-safe:animate-pulse"
          }`}
        />
        {t(stale ? keys.stale : keys.text)}
      </span>
    </Tooltip>
  );
}

// Wraps a button that is disabled because prices are pending in a tooltip
// (a disabled <button> fires no pointer events, so the tooltip must sit on
// a wrapper). When `locked` is false the child is returned AS IS - no
// wrapper element, no attribute - so the flag-off tree is unchanged.
// variant "upload": the clone-preparing tooltip, gated on its own flag.
export function ProductPendingLock({
  locked,
  children,
  className,
  variant = "product",
}: {
  locked: boolean;
  children: ReactElement;
  className?: string;
  variant?: LogoActionLockVariant;
}): ReactElement {
  const { t } = useTranslation();
  if (!variantEnabled(variant) || !locked) return children;
  return (
    <Tooltip title={t(BADGE_KEYS[variant].action)} placement="top">
      <span
        data-testid="product-pending-lock"
        className={className ?? "inline-block w-full"}
      >
        {children}
      </span>
    </Tooltip>
  );
}
